// Multiple independent things can want the Intercom *launcher* (the default
// floating bubble) hidden at the same time: a full-screen route
// (`useHideIntercom`, possibly from more than one component on the same
// page — see GamePage+LiveGame both calling it on /live) and the org/plan
// visibility rule (`useIdentifyMetrics`). The launcher must stay hidden as
// long as ANY reason is still active — losing one reason (e.g. leaving a
// hidden route) must not reveal it if another (e.g. the org is gated) still
// applies. A plain boolean can't represent that; a set of reason tokens can.
const hideReasons = new Set<unknown>();

type Listener = (shouldHide: boolean) => void;
const listeners = new Set<Listener>();

function notify(): void {
  const shouldHide = hideReasons.size > 0;
  listeners.forEach(listener => listener(shouldHide));
}

export function addIntercomHideReason(reason: unknown): void {
  if (hideReasons.has(reason)) return;
  hideReasons.add(reason);
  notify();
}

export function removeIntercomHideReason(reason: unknown): void {
  if (!hideReasons.has(reason)) return;
  hideReasons.delete(reason);
  notify();
}

export function isIntercomLauncherHidden(): boolean {
  return hideReasons.size > 0;
}

export function onIntercomHideReasonsChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Test-only: reset the module singleton between test cases. Never call this
// from app code.
export function __resetIntercomLauncherVisibilityForTests(): void {
  hideReasons.clear();
  listeners.clear();
}
